import { useState, useRef, useEffect, useCallback } from 'react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { X, Calendar, Clock, Hash, User, UserPlus, Send, MessageSquare, Pencil, Check as CheckIcon, ChevronDown, Globe, AlertTriangle, Paperclip, Image, Loader2, Building2, Layers, History, ArrowRight, Trash2 } from 'lucide-react'
import { cn } from '@/lib/utils'
import { Badge } from '@/components/common/Badge'
import { WorkflowStepper, type WorkflowStep } from './WorkflowStepper'
import { AssetPreview, AssetIcon, formatFileSize } from './AssetPreview'
import {
  PLATFORM_COLORS,
  PLATFORM_LABEL,
  CONTENT_TYPE_LABEL,
  STATUS_LABEL,
  STATUS_DOT_COLOR,
} from './ContentCard'
import { pedApi, usersApi, axiosInstance, getApiErrorMessage } from '@/services/api'
import { useAuthStore } from '@/stores/authStore'
import type { Content, ContentStatus, ContentType, Platform } from '@/types'

// Map content status to workflow step (senza guest — con step "Approvato" interno distinto)
const STATUS_TO_STEP_BASE: Record<ContentStatus, number> = {
  IDEA: 1, TODO: 1, IN_PRODUCTION: 2, READY: 2, IN_REVIEW: 3, CHANGES_REQUESTED: 2,
  APPROVED: 4, CLIENT_APPROVED: 4, SCHEDULED: 5, PUBLISHED: 6,
}

// Step base (progetti senza guest): l'approvazione interna ("Approvato") è uno step a sé,
// distinto da "Pubblica" (SCHEDULED), così è raggiungibile e coerente col pulsante Approva.
const WORKFLOW_STEPS_BASE: WorkflowStep[] = [
  { id: 1, label: 'Crea', role: 'Operativo' },
  { id: 2, label: 'Produci', role: 'Copywriter / Designer' },
  { id: 3, label: 'In revisione', role: 'Manager' },
  { id: 4, label: 'Approvato', role: 'Manager' },
  { id: 5, label: 'Pubblica', role: 'Social Manager' },
  { id: 6, label: 'Pubblicato', role: '' },
]

// Map content status to workflow step (con guest: step "In revisione" + "Approvato" + "Cliente")
const STATUS_TO_STEP_WITH_GUESTS: Record<ContentStatus, number> = {
  IDEA: 1, TODO: 1, IN_PRODUCTION: 2, READY: 2, CHANGES_REQUESTED: 2, IN_REVIEW: 3,
  APPROVED: 4, CLIENT_APPROVED: 5, SCHEDULED: 6, PUBLISHED: 7,
}

const WORKFLOW_STEPS_WITH_GUESTS: WorkflowStep[] = [
  { id: 1, label: 'Crea', role: 'Operativo' },
  { id: 2, label: 'Produci', role: 'Copywriter / Designer' },
  { id: 3, label: 'In revisione', role: 'Manager' },
  { id: 4, label: 'Approvato', role: 'Manager' },
  { id: 5, label: 'Cliente', role: 'Approvazione cliente' },
  { id: 6, label: 'Pubblica', role: 'Social Manager' },
  { id: 7, label: 'Pubblicato', role: '' },
]

const STEP_TO_STATUS_BASE: Record<number, ContentStatus> = {
  1: 'IDEA', 2: 'IN_PRODUCTION', 3: 'IN_REVIEW', 4: 'APPROVED', 5: 'SCHEDULED', 6: 'PUBLISHED',
}

const STEP_TO_STATUS_WITH_GUESTS: Record<number, ContentStatus> = {
  1: 'IDEA', 2: 'IN_PRODUCTION', 3: 'IN_REVIEW', 4: 'APPROVED', 5: 'CLIENT_APPROVED', 6: 'SCHEDULED', 7: 'PUBLISHED',
}

const ALL_PLATFORMS: Platform[] = ['INSTAGRAM', 'FACEBOOK', 'TIKTOK', 'LINKEDIN', 'YOUTUBE', 'TWITTER', 'PINTEREST']
const ALL_CONTENT_TYPES: ContentType[] = ['POST', 'REEL', 'STORY', 'CAROUSEL', 'VIDEO', 'ARTICLE', 'INFOGRAPHIC']

const PLATFORM_FULL_LABEL: Record<Platform, string> = {
  INSTAGRAM: 'Instagram',
  FACEBOOK: 'Facebook',
  TIKTOK: 'TikTok',
  LINKEDIN: 'LinkedIn',
  TWITTER: 'Twitter/X',
  YOUTUBE: 'YouTube',
  PINTEREST: 'Pinterest',
}

// Normalize hashtags: backend may return string or string[]
function hashtagsToArray(h: unknown): string[] {
  if (Array.isArray(h)) return h
  if (typeof h === 'string' && h.trim()) return h.split(/[,\s]+/).map(t => t.replace(/^#/, '').trim()).filter(Boolean)
  return []
}

function hashtagsToString(h: unknown): string {
  return hashtagsToArray(h).join(', ')
}

// Comment type labels & colors
const COMMENT_TYPE_STYLE: Record<string, { label: string; color: string }> = {
  change_request: { label: 'Richiesta modifiche', color: 'bg-warning-100 text-warning-700 border-warning-200' },
  approval: { label: 'Approvazione', color: 'bg-success-100 text-success-700 border-success-200' },
  comment: { label: '', color: '' },
}

interface ContentComment {
  id: string
  text: string
  type: string
  createdAt: string
  user: { id: string; firstName: string; lastName: string; avatar: string | null }
}

interface ContentVersionAsset { url: string; filename: string; mimeType: string; type: string }
interface ContentVersion {
  id: string
  versionNo: number
  stage: 'before' | 'after'
  reworkCycle: number
  title: string | null
  caption: string | null
  body: string | null
  hashtags: string | null
  assets: ContentVersionAsset[] | null
  createdAt: string
  createdBy: { id: string; firstName: string; lastName: string; avatar: string | null } | null
}

// ── Content Assets Section ──
interface UploadProgress {
  fileName: string
  percent: number
  speed: string // e.g. "2.4 MB/s"
  loaded: string
  total: string
}

function formatSpeed(bytesPerSec: number): string {
  if (bytesPerSec < 1024) return `${bytesPerSec.toFixed(0)} B/s`
  if (bytesPerSec < 1024 * 1024) return `${(bytesPerSec / 1024).toFixed(1)} KB/s`
  return `${(bytesPerSec / (1024 * 1024)).toFixed(1)} MB/s`
}

function ContentAssetsSection({ contentId }: { contentId: string }) {
  const queryClient = useQueryClient()
  const fileInputRef = useRef<HTMLInputElement>(null)
  const [uploading, setUploading] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [uploadProgress, setUploadProgress] = useState<UploadProgress | null>(null)
  const uploadStartRef = useRef<number>(0)

  // Fetch content detail to get fresh assets list
  const { data: freshContent, refetch: refetchAssets } = useQuery({
    queryKey: ['content-detail-assets', contentId],
    queryFn: async () => {
      const res = await axiosInstance.get(`/ped/contents/${contentId}`)
      const d = res?.data?.data ?? res?.data ?? res
      return d
    },
    staleTime: 0,
  })

  const assets: Array<{ id: string; filename: string; mimeType: string; size: number }> =
    freshContent?.assets ?? []

  const deleteMutation = useMutation({
    mutationFn: (assetId: string) => pedApi.deleteAsset(assetId),
    onSuccess: () => {
      void refetchAssets()
      void queryClient.invalidateQueries({ queryKey: ['ped-contents'] })
    },
  })

  const handleUpload = useCallback(async (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = e.target.files
    if (!files || files.length === 0) return
    setUploading(true)
    setError(null)
    try {
      const fileList = Array.from(files)
      for (let i = 0; i < fileList.length; i++) {
        const file = fileList[i]!
        uploadStartRef.current = Date.now()
        setUploadProgress({
          fileName: file.name,
          percent: 0,
          speed: '—',
          loaded: '0 B',
          total: formatFileSize(file.size),
        })
        await pedApi.uploadAsset(contentId, file, (evt) => {
          const total = evt.total ?? file.size
          const percent = Math.round((evt.loaded / total) * 100)
          const elapsed = (Date.now() - uploadStartRef.current) / 1000
          const speed = elapsed > 0 ? evt.loaded / elapsed : 0
          setUploadProgress({
            fileName: file.name,
            percent,
            speed: formatSpeed(speed),
            loaded: formatFileSize(evt.loaded),
            total: formatFileSize(total),
          })
        })
      }
      setUploadProgress(null)
      void refetchAssets()
      void queryClient.invalidateQueries({ queryKey: ['ped-contents'] })
    } catch (err) {
      setError(getApiErrorMessage(err))
      setUploadProgress(null)
    }
    setUploading(false)
    if (fileInputRef.current) fileInputRef.current.value = ''
  }, [contentId, queryClient, refetchAssets])

  function handleDelete(assetId: string) {
    deleteMutation.mutate(assetId)
  }

  return (
    <div>
      <p className="text-xs font-semibold text-slate-500 uppercase tracking-wide mb-2 flex items-center gap-1.5">
        <Paperclip className="w-3.5 h-3.5" />
        Media & Allegati
        {assets.length > 0 && (
          <span className="text-[10px] text-slate-400 font-normal ml-1">({assets.length})</span>
        )}
      </p>

      {assets.length > 0 && (
        <div className="space-y-3 mb-3">
          {assets.map((asset) => (
            <AssetPreview
              key={asset.id}
              asset={asset}
              onDelete={handleDelete}
              deleting={deleteMutation.isPending}
            />
          ))}
        </div>
      )}

      <input
        ref={fileInputRef}
        type="file"
        multiple
        accept="image/*,video/*,application/pdf,.doc,.docx,.xls,.xlsx,.ppt,.pptx"
        className="hidden"
        onChange={handleUpload}
      />
      {uploadProgress && (
        <div className="mb-2 rounded-lg border border-primary-200 bg-primary-50/50 p-3">
          <div className="flex items-center justify-between mb-1.5">
            <span className="text-xs font-medium text-slate-700 truncate max-w-[60%]" title={uploadProgress.fileName}>
              {uploadProgress.fileName}
            </span>
            <span className="text-[10px] text-slate-500 font-mono tabular-nums">
              {uploadProgress.loaded} / {uploadProgress.total}
            </span>
          </div>
          <div className="w-full h-2 bg-slate-200 rounded-full overflow-hidden">
            <div
              className="h-full bg-primary-600 rounded-full transition-all duration-300 ease-out"
              style={{ width: `${uploadProgress.percent}%` }}
            />
          </div>
          <div className="flex items-center justify-between mt-1">
            <span className="text-[10px] text-primary-700 font-semibold tabular-nums">{uploadProgress.percent}%</span>
            <span className="text-[10px] text-slate-500 tabular-nums">{uploadProgress.speed}</span>
          </div>
        </div>
      )}
      <button
        type="button"
        onClick={() => fileInputRef.current?.click()}
        disabled={uploading}
        className={cn(
          'w-full flex items-center justify-center gap-2 px-3 py-3 text-xs font-medium',
          'border-2 border-dashed border-slate-300 rounded-lg',
          'text-slate-500 hover:text-primary-600 hover:border-primary-400 hover:bg-primary-50/50 transition-colors',
          'disabled:opacity-50 disabled:cursor-not-allowed'
        )}
      >
        {uploading && !uploadProgress ? (
          <>
            <Loader2 className="w-4 h-4 animate-spin" />
            Caricamento in corso...
          </>
        ) : uploading ? (
          <>
            <Loader2 className="w-4 h-4 animate-spin" />
            Caricamento in corso...
          </>
        ) : (
          <>
            <Image className="w-4 h-4" />
            Carica immagine, video o file
          </>
        )}
      </button>
      {error && (
        <p className="text-xs text-danger-600 mt-1.5">{error}</p>
      )}
    </div>
  )
}

interface ContentDetailModalProps {
  content: Content
  onClose: () => void
  onStatusChange?: (contentId: string, newStatus: ContentStatus) => void
  onUpdate?: (contentId: string, data: Record<string, unknown>) => void
  /** 'modal' = slide-over fisso con backdrop (default); 'panel' = inline senza backdrop */
  variant?: 'modal' | 'panel'
}

export function ContentDetailModal({ content, onClose, onStatusChange, onUpdate, variant = 'modal' }: ContentDetailModalProps) {
  const queryClient = useQueryClient()
  const authUser = useAuthStore((state) => state.user)
  const userLevel = authUser?.role?.level ?? 4
  const platforms = content.platforms ?? []
  // Fetch full content detail (includes project team to detect guests)
  const { data: fullContent } = useQuery({
    queryKey: ['content-detail-assets', content.id],
    queryFn: async () => {
      const res = await axiosInstance.get(`/ped/contents/${content.id}`)
      return res?.data?.data ?? res?.data ?? res
    },
    staleTime: 0,
  })
  const hasGuests = fullContent?.editorialPlan?.project?.team?.some((m: { user: { isGuest: boolean } }) => m.user.isGuest) ?? false
  const statusToStep = hasGuests ? STATUS_TO_STEP_WITH_GUESTS : STATUS_TO_STEP_BASE
  const stepToStatus = hasGuests ? STEP_TO_STATUS_WITH_GUESTS : STEP_TO_STATUS_BASE
  const workflowSteps = hasGuests ? WORKFLOW_STEPS_WITH_GUESTS : WORKFLOW_STEPS_BASE
  const currentStep = statusToStep[content.status] ?? 1

  // Editing states
  const [editingField, setEditingField] = useState<string | null>(null)
  const [editTitle, setEditTitle] = useState(content.title)
  const [editCaption, setEditCaption] = useState(content.caption ?? content.body ?? '')
  const [editHashtags, setEditHashtags] = useState(hashtagsToString(content.hashtags))
  const [editScheduledAt, setEditScheduledAt] = useState(
    content.scheduledAt ? new Date(content.scheduledAt).toISOString().slice(0, 16) : ''
  )
  const [editPlatforms, setEditPlatforms] = useState<Set<Platform>>(new Set(platforms))
  const [showPlatformPicker, setShowPlatformPicker] = useState(false)
  const [editType, setEditType] = useState<ContentType>(content.type)
  const [showTypePicker, setShowTypePicker] = useState(false)
  const [showFormatDetail, setShowFormatDetail] = useState(false)

  // Eliminazione contenuto: L1-L3 hanno ped:delete, il Social Media Manager (L4) ha il flag
  // canDeletePed sul ruolo. Il backend blocca comunque i contenuti già pubblicati e quelli
  // fuori dal proprio perimetro (solo i contenuti dei progetti di cui si fa parte).
  const canDeleteContent =
    (authUser?.isSuperAdmin ||
      authUser?.role?.canDeletePed ||
      authUser?.role?.permissions?.ped?.includes('delete') ||
      false) && content.status !== 'PUBLISHED'
  const [deleting, setDeleting] = useState(false)

  // Comment states
  const [commentText, setCommentText] = useState('')
  const [changeRequestMode, setChangeRequestMode] = useState(false)
  const [approveCommentMode, setApproveCommentMode] = useState(false)

  const titleRef = useRef<HTMLInputElement>(null)
  const captionRef = useRef<HTMLTextAreaElement>(null)
  const commentInputRef = useRef<HTMLInputElement>(null)
  const platformPickerRef = useRef<HTMLDivElement>(null)
  const typePickerRef = useRef<HTMLDivElement>(null)
  const formatDetailRef = useRef<HTMLDivElement>(null)
  const commentsEndRef = useRef<HTMLDivElement>(null)

  // Fetch comments
  const { data: commentsData } = useQuery({
    queryKey: ['content-comments', content.id],
    queryFn: async () => {
      const res = await pedApi.getContentComments(content.id)
      return (res?.data ?? res ?? []) as ContentComment[]
    },
  })
  const comments = commentsData ?? []

  // Fetch storico rilavorazioni (snapshot vecchio/rilavorato)
  const { data: versionsData } = useQuery({
    queryKey: ['content-versions', content.id],
    queryFn: async () => {
      const res = await pedApi.getContentVersions(content.id)
      return (res?.data ?? res ?? []) as ContentVersion[]
    },
  })
  const versions = versionsData ?? []
  const [showHistory, setShowHistory] = useState(false)

  // Ricarica lo storico quando cambia lo stato (es. dopo "Rilavorare" o re-invio in revisione)
  useEffect(() => {
    void queryClient.invalidateQueries({ queryKey: ['content-versions', content.id] })
  }, [content.status, content.id, queryClient])

  // Fetch users for assignment dropdowns
  const { data: usersData } = useQuery({
    queryKey: ['users-list-all'],
    queryFn: async () => {
      const res = await usersApi.list({ limit: 200 })
      return (res?.data ?? res ?? []) as Array<{ id: string; firstName: string; lastName: string; role?: { name: string } }>
    },
    staleTime: 60_000,
  })
  const allUsers = usersData ?? []

  // Assignment picker states
  const [showAssignedPicker, setShowAssignedPicker] = useState(false)
  const [showPublisherPicker, setShowPublisherPicker] = useState(false)

  // Add comment mutation
  const addCommentMutation = useMutation({
    mutationFn: ({ text, type }: { text: string; type: string }) =>
      pedApi.addContentComment(content.id, text, type),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['content-comments', content.id] })
      setCommentText('')
      setTimeout(() => commentsEndRef.current?.scrollIntoView({ behavior: 'smooth' }), 100)
    },
  })

  // Focus input when editing starts
  useEffect(() => {
    if (editingField === 'title') titleRef.current?.focus()
    if (editingField === 'caption') captionRef.current?.focus()
  }, [editingField])

  // Focus comment input when change request / approve-with-comment mode is activated
  useEffect(() => {
    if (changeRequestMode || approveCommentMode) {
      commentInputRef.current?.focus()
    }
  }, [changeRequestMode, approveCommentMode])

  // Close platform/type picker on outside click
  useEffect(() => {
    function handleClick(e: MouseEvent) {
      if (showPlatformPicker && platformPickerRef.current && !platformPickerRef.current.contains(e.target as Node)) {
        savePlatforms()
      }
      if (showTypePicker && typePickerRef.current && !typePickerRef.current.contains(e.target as Node)) {
        saveType()
      }
      if (showFormatDetail && formatDetailRef.current && !formatDetailRef.current.contains(e.target as Node)) {
        setShowFormatDetail(false)
      }
    }
    document.addEventListener('mousedown', handleClick)
    return () => document.removeEventListener('mousedown', handleClick)
  })

  function saveField(field: string, value: unknown) {
    setEditingField(null)
    if (!onUpdate) return
    onUpdate(content.id, { [field]: value })
  }

  function saveTitle() {
    const trimmed = editTitle.trim()
    if (trimmed && trimmed !== content.title) {
      saveField('title', trimmed)
    } else {
      setEditTitle(content.title)
      setEditingField(null)
    }
  }

  function saveCaption() {
    const trimmed = editCaption.trim()
    if (trimmed !== (content.caption ?? content.body ?? '')) {
      saveField('caption', trimmed)
    }
    setEditingField(null)
  }

  function saveHashtags() {
    const tags = editHashtags
      .split(/[,\s]+/)
      .map(t => t.replace(/^#/, '').trim())
      .filter(Boolean)
    const original = hashtagsToArray(content.hashtags).join(',')
    if (tags.join(',') !== original) {
      saveField('hashtags', tags)
    }
    setEditingField(null)
  }

  function saveSchedule() {
    const newVal = editScheduledAt ? new Date(editScheduledAt).toISOString() : null
    const oldVal = content.scheduledAt ?? null
    if (newVal !== oldVal) {
      saveField('scheduledAt', newVal)
    }
    setEditingField(null)
  }

  function savePlatforms() {
    setShowPlatformPicker(false)
    const selected = [...editPlatforms] as Platform[]
    if (selected.length === 0) return
    const oldSet = new Set(platforms)
    const changed = selected.length !== oldSet.size || selected.some(p => !oldSet.has(p))
    if (changed && onUpdate) {
      onUpdate(content.id, { platforms: selected })
    }
  }

  function saveType() {
    setShowTypePicker(false)
    if (editType !== content.type && onUpdate) {
      onUpdate(content.id, { type: editType })
    }
  }

  function handleStepClick(stepId: number) {
    const newStatus = stepToStatus[stepId]
    if (newStatus && newStatus !== content.status && onStatusChange) {
      onStatusChange(content.id, newStatus)
    }
  }

  // Submit comment (or change request)
  function handleSubmitComment() {
    const trimmed = commentText.trim()
    if (!trimmed) return

    if (changeRequestMode) {
      // Salva come change_request, poi rimanda al team (CHANGES_REQUESTED)
      addCommentMutation.mutate(
        { text: trimmed, type: 'change_request' },
        {
          onSuccess: () => {
            if (onStatusChange) {
              onStatusChange(content.id, 'CHANGES_REQUESTED')
            }
            setChangeRequestMode(false)
          },
        }
      )
    } else if (approveCommentMode) {
      // Salva la nota come commento, poi approva (APPROVED)
      addCommentMutation.mutate(
        { text: trimmed, type: 'comment' },
        {
          onSuccess: () => {
            if (onStatusChange) {
              onStatusChange(content.id, 'APPROVED')
            }
            setApproveCommentMode(false)
          },
        }
      )
    } else {
      addCommentMutation.mutate({ text: trimmed, type: 'comment' })
    }
  }

  function handleApprove() {
    if (onStatusChange) onStatusChange(content.id, 'APPROVED')
  }

  function handleAdvance() {
    const next = effectiveNextStatus
    if (!next || !onStatusChange) return

    // Il commento di approvazione (chi e quando) viene registrato lato backend in
    // advanceContentStatus quando lo stato diventa APPROVED/CLIENT_APPROVED.
    onStatusChange(content.id, next)
  }

  function handleRequestChanges() {
    setChangeRequestMode(true)
    // Focus will happen via useEffect
  }

  async function handleDeleteContent() {
    const confirmed = window.confirm(
      `Eliminare "${content.title}"?\n\nVengono rimossi anche allegati, commenti e storico delle rilavorazioni. L'operazione non è reversibile.`
    )
    if (!confirmed) return
    setDeleting(true)
    try {
      await pedApi.deleteContent(content.id)
      void queryClient.invalidateQueries({ queryKey: ['ped-contents'] })
      void queryClient.invalidateQueries({ queryKey: ['calendar-events'] })
      onClose()
    } catch (err) {
      window.alert(getApiErrorMessage(err))
    } finally {
      setDeleting(false)
    }
  }

  const scheduledDate = content.scheduledAt
    ? new Date(content.scheduledAt).toLocaleDateString('it-IT', {
        weekday: 'long',
        day: '2-digit',
        month: 'long',
        year: 'numeric',
      })
    : null

  const scheduledTime = content.scheduledAt
    ? new Date(content.scheduledAt).toLocaleTimeString('it-IT', {
        hour: '2-digit',
        minute: '2-digit',
      })
    : null

  const nextStatus: Partial<Record<ContentStatus, ContentStatus>> = {
    IDEA: 'TODO',
    TODO: 'IN_PRODUCTION',
    IN_PRODUCTION: 'READY',
    READY: 'IN_REVIEW',
    IN_REVIEW: 'APPROVED',
    CHANGES_REQUESTED: 'IN_PRODUCTION',
    APPROVED: 'SCHEDULED',
    SCHEDULED: 'PUBLISHED',
  }

  const advanceLabel: Partial<Record<ContentStatus, string>> = {
    IDEA: 'Avvia',
    TODO: 'In Produzione',
    IN_PRODUCTION: 'Pronto',
    READY: 'Invia in Review',
    IN_REVIEW: 'Approva',
    CHANGES_REQUESTED: 'Rilavorare',
    APPROVED: 'Programma',
    SCHEDULED: 'Pubblica',
  }

  // Rework: un contenuto già passato in revisione (ha almeno una richiesta modifiche) deve
  // poter tornare in approvazione con UN click esplicito, senza dover passare da "Pronto"
  // né dipendere dall'auto-avanzamento implicito. Vale in "In produzione" e "Pronto".
  const hasChangeRequest = comments.some((c: { type?: string }) => c.type === 'change_request')
  const isReworkInProgress = hasChangeRequest && (content.status === 'IN_PRODUCTION' || content.status === 'READY')
  const effectiveNextStatus: ContentStatus | undefined = isReworkInProgress ? 'IN_REVIEW' : nextStatus[content.status]
  const effectiveAdvanceLabel: string | undefined = isReworkInProgress ? 'Manda in approvazione' : advanceLabel[content.status]

  // Solo il project manager puo' approvare/rifiutare
  const projectManagerId = content.editorialPlan?.project?.managerId
  const isProjectManager = !!projectManagerId && authUser?.id === projectManagerId
  const canApproveOrReject = isProjectManager || userLevel <= 2

  // "Richiedi Modifiche" only available when in review/approved
  const canRequestChanges = canApproveOrReject && (content.status === 'IN_REVIEW' || content.status === 'APPROVED')

  return (
    <>
      {variant === 'modal' && (
        <div className="fixed inset-0 bg-black/40 z-40 animate-fade-in" onClick={onClose} />
      )}
      <div className={variant === 'panel'
        ? 'flex flex-col h-full bg-white rounded-2xl border border-slate-200 shadow-card overflow-hidden'
        : 'fixed inset-y-0 right-0 z-50 w-full max-w-xl bg-white shadow-modal flex flex-col animate-slide-in-right'
      }>

        {/* ── Header: Title + close ── */}
        <div className="px-4 sm:px-6 pt-4 sm:pt-5 pb-3 sm:pb-4 border-b border-slate-200">
          <div className="flex items-start justify-between gap-3">
            <div className="flex-1 min-w-0">
              {editingField === 'title' ? (
                <input
                  ref={titleRef}
                  value={editTitle}
                  onChange={(e) => setEditTitle(e.target.value)}
                  onBlur={saveTitle}
                  onKeyDown={(e) => { if (e.key === 'Enter') saveTitle(); if (e.key === 'Escape') { setEditTitle(content.title); setEditingField(null) } }}
                  className="text-lg font-semibold text-slate-900 leading-tight w-full bg-transparent border-b-2 border-primary-500 outline-none py-0.5"
                />
              ) : (
                <h2
                  onClick={() => { setEditTitle(content.title); setEditingField('title') }}
                  className="text-lg font-semibold text-slate-900 leading-tight cursor-pointer hover:text-primary-700 transition-colors group flex items-center gap-1.5"
                  title="Clicca per modificare"
                >
                  {content.title}
                  <Pencil className="w-3.5 h-3.5 text-slate-300 opacity-0 group-hover:opacity-100 transition-opacity flex-shrink-0" />
                </h2>
              )}

              <div className="flex items-center gap-2 sm:gap-2.5 mt-2 flex-wrap">
                <div className="relative" ref={typePickerRef}>
                  <button
                    onClick={() => { setEditType(content.type); setShowTypePicker(!showTypePicker) }}
                    className="flex items-center gap-1 cursor-pointer hover:opacity-70 transition-opacity"
                  >
                    <Badge variant="neutral" size="sm">
                      {CONTENT_TYPE_LABEL[content.type] ?? content.type}
                      <ChevronDown className="w-2.5 h-2.5 ml-0.5 inline" />
                    </Badge>
                  </button>
                  {showTypePicker && (
                    <div className="absolute top-full left-0 mt-1 bg-white border border-slate-200 rounded-lg shadow-dropdown p-1 z-50 min-w-[140px]">
                      {ALL_CONTENT_TYPES.map(t => (
                        <button
                          key={t}
                          onClick={() => { setEditType(t); setShowTypePicker(false); if (t !== content.type && onUpdate) onUpdate(content.id, { type: t }) }}
                          className={cn(
                            'w-full text-left px-3 py-1.5 text-xs rounded hover:bg-slate-50 transition-colors',
                            t === content.type && 'bg-primary-50 text-primary-700 font-medium'
                          )}
                        >
                          {CONTENT_TYPE_LABEL[t]}
                        </button>
                      ))}
                    </div>
                  )}
                </div>

                <div className="w-px h-4 bg-slate-200" />

                <div className="flex items-center gap-1.5">
                  <div className={cn('w-2 h-2 rounded-full flex-shrink-0', STATUS_DOT_COLOR[content.status])} />
                  <span className="text-xs text-slate-600 font-medium">
                    {STATUS_LABEL[content.status]}
                  </span>
                </div>

              </div>

              {(content.client?.name || content.editorialPlan?.project?.name) && (
                <div className="flex items-center gap-1.5 mt-1.5">
                  <Building2 className="w-3 h-3 text-slate-400 flex-shrink-0" />
                  <span className="text-xs text-slate-500">
                    {[content.client?.name, content.editorialPlan?.project?.name].filter(Boolean).join(' · ')}
                  </span>
                </div>
              )}
            </div>

            <div className="flex items-center gap-0.5 flex-shrink-0">
              {canDeleteContent && (
                <button
                  onClick={() => void handleDeleteContent()}
                  disabled={deleting}
                  title="Elimina contenuto"
                  className="p-1.5 rounded-md text-slate-400 hover:text-danger-600 hover:bg-danger-50 transition-colors disabled:opacity-40"
                >
                  {deleting ? <Loader2 className="w-5 h-5 animate-spin" /> : <Trash2 className="w-5 h-5" />}
                </button>
              )}
              <button
                onClick={onClose}
                className="p-1.5 rounded-md text-slate-400 hover:text-slate-600 hover:bg-slate-100 transition-colors"
              >
                <X className="w-5 h-5" />
              </button>
            </div>
          </div>
        </div>

        {/* ── Body (scrollable) ── */}
        <div className="flex-1 overflow-y-auto">

          {/* 1. Piattaforme */}
          <div className="px-4 sm:px-6 py-3 sm:py-4 border-b border-slate-100" ref={platformPickerRef}>
            <div className="flex items-center justify-between mb-2.5">
              <p className="text-xs font-semibold text-slate-500 uppercase tracking-wide flex items-center gap-1.5">
                <Globe className="w-3.5 h-3.5" />
                Piattaforme di pubblicazione
              </p>
              <button
                onClick={() => { setEditPlatforms(new Set(platforms)); setShowPlatformPicker(!showPlatformPicker) }}
                className="text-[10px] text-primary-600 hover:text-primary-700 font-medium transition-colors"
              >
                Modifica
              </button>
            </div>
            <div className="flex flex-wrap gap-1.5">
              {platforms.map(p => (
                <span
                  key={p}
                  className="inline-flex items-center gap-1.5 text-xs font-medium px-2.5 py-1 rounded-full text-white"
                  style={{ backgroundColor: PLATFORM_COLORS[p] ?? '#64748B' }}
                >
                  <span className="text-[10px] font-bold opacity-80">{PLATFORM_LABEL[p]}</span>
                  {PLATFORM_FULL_LABEL[p]}
                </span>
              ))}
              {platforms.length === 0 && (
                <span className="text-xs text-slate-400 italic">Nessuna piattaforma selezionata</span>
              )}
            </div>
            {showPlatformPicker && (
              <div className="mt-2 bg-white border border-slate-200 rounded-lg shadow-dropdown p-2">
                {ALL_PLATFORMS.map(p => (
                  <label key={p} className="flex items-center gap-2.5 px-2 py-1.5 hover:bg-slate-50 rounded cursor-pointer">
                    <input
                      type="checkbox"
                      checked={editPlatforms.has(p)}
                      onChange={() => {
                        const next = new Set(editPlatforms)
                        if (next.has(p)) next.delete(p); else next.add(p)
                        if (next.size > 0) setEditPlatforms(next)
                      }}
                      className="rounded border-slate-300 text-primary-600 focus:ring-primary-500"
                    />
                    <span className="w-3 h-3 rounded-full flex-shrink-0" style={{ backgroundColor: PLATFORM_COLORS[p] }} />
                    <span className="text-sm text-slate-700">{PLATFORM_FULL_LABEL[p]}</span>
                  </label>
                ))}
                <button
                  onClick={savePlatforms}
                  className="w-full mt-2 px-3 py-1.5 text-xs font-medium text-white bg-primary-600 rounded-lg hover:bg-primary-700 transition-colors"
                >
                  Conferma ({editPlatforms.size})
                </button>
              </div>
            )}
          </div>

          {/* 2. Workflow */}
          <div className="px-4 sm:px-6 py-3 sm:py-4 bg-slate-50/70 border-b border-slate-100">
            <div className="flex items-center justify-between mb-3">
              <p className="text-xs font-semibold text-slate-500 uppercase tracking-wide">Workflow</p>
              <span className="text-[10px] text-slate-400">Clicca uno step per cambiare stato</span>
            </div>
            <WorkflowStepper currentStep={currentStep} onStepClick={handleStepClick} {...(workflowSteps ? { steps: workflowSteps } : {})} />
          </div>

          {/* 2b. Revisione — banner + chat */}
          {content.status === 'CHANGES_REQUESTED' && (() => {
            // Primary: look for change_request comments; fallback: any comment works as instruction
            const changeRequests = comments.filter((c: any) => c.type === 'change_request')
            const lastCR = changeRequests[changeRequests.length - 1]
            // Thread: all comments after the main instruction
            const lastCRIndex = lastCR ? comments.findIndex((c: any) => c.id === lastCR.id) : -1
            const thread = lastCRIndex >= 0 ? comments.slice(lastCRIndex + 1) : (lastCR ? [] : comments)
            return (
              <div className="mx-4 sm:mx-6 mt-4 mb-0 rounded-lg border border-danger-200 overflow-hidden">
                {/* Header: richiesta modifiche */}
                <div className="p-3 bg-danger-50">
                  <div className="flex items-start gap-2">
                    <AlertTriangle className="w-4 h-4 text-danger-500 flex-shrink-0 mt-0.5" />
                    <div className="flex-1 min-w-0">
                      <p className="text-xs font-semibold text-danger-700 uppercase tracking-wide mb-1">Modifiche richieste</p>
                      {lastCR ? (
                        <>
                          <p className="text-sm text-danger-700 leading-relaxed">{lastCR.text}</p>
                          <p className="text-[10px] text-danger-500 mt-1.5 tabular-nums">
                            {lastCR.user.firstName} {lastCR.user.lastName} — {new Date(lastCR.createdAt).toLocaleDateString('it-IT', { day: '2-digit', month: 'short' })} alle {new Date(lastCR.createdAt).toLocaleTimeString('it-IT', { hour: '2-digit', minute: '2-digit' })}
                          </p>
                        </>
                      ) : canApproveOrReject ? (
                        <p className="text-sm text-danger-700 leading-relaxed font-medium">Scrivi sotto quali modifiche servono, così l'operativo sa cosa fare.</p>
                      ) : (
                        <p className="text-sm text-danger-700 leading-relaxed">In attesa di istruzioni dal responsabile. Puoi chiedere chiarimenti sotto.</p>
                      )}
                    </div>
                  </div>
                </div>

                {/* Thread messaggi */}
                {thread.length > 0 && (
                  <div className="px-3 py-2 space-y-2 bg-danger-50/30 border-t border-danger-100 max-h-40 overflow-y-auto">
                    {thread.map((c: any) => (
                      <div key={c.id} className="flex items-start gap-2">
                        <div className="w-5 h-5 rounded-full bg-primary-600 flex items-center justify-center flex-shrink-0">
                          <span className="text-[8px] font-semibold text-white">
                            {c.user.firstName.charAt(0)}{c.user.lastName.charAt(0)}
                          </span>
                        </div>
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-1.5">
                            <span className="text-xs font-medium text-slate-700">{c.user.firstName} {c.user.lastName}</span>
                            <span className="text-[10px] text-slate-400">
                              {new Date(c.createdAt).toLocaleTimeString('it-IT', { hour: '2-digit', minute: '2-digit' })}
                            </span>
                          </div>
                          <p className="text-sm text-slate-600">{c.text}</p>
                        </div>
                      </div>
                    ))}
                  </div>
                )}

                {/* Input messaggio */}
                <div className="flex gap-2 p-2 bg-white border-t border-danger-100">
                  <input
                    type="text"
                    placeholder={canApproveOrReject ? (lastCR ? 'Aggiungi dettaglio...' : 'Scrivi quali modifiche servono...') : 'Chiedi chiarimenti...'}
                    className="flex-1 text-sm px-2.5 py-1.5 border border-slate-200 rounded-lg bg-white focus:outline-none focus:ring-1 focus:ring-primary-400 focus:border-primary-400 placeholder:text-slate-400"
                    onKeyDown={(e) => {
                      if (e.key === 'Enter') {
                        const val = (e.target as HTMLInputElement).value.trim()
                        if (val) {
                          const commentType = (canApproveOrReject && !lastCR) ? 'change_request' : 'comment'
                          addCommentMutation.mutate({ text: val, type: commentType })
                          ;(e.target as HTMLInputElement).value = ''
                        }
                      }
                    }}
                  />
                  <button
                    type="button"
                    onClick={(e) => {
                      const input = (e.currentTarget.previousElementSibling as HTMLInputElement)
                      const val = input?.value.trim()
                      if (val) {
                        const commentType = (canApproveOrReject && !lastCR) ? 'change_request' : 'comment'
                        addCommentMutation.mutate({ text: val, type: commentType })
                        input.value = ''
                      }
                    }}
                    className="p-1.5 text-white bg-primary-600 rounded-lg hover:bg-primary-700 transition-colors"
                  >
                    <Send className="w-3.5 h-3.5" />
                  </button>
                </div>
              </div>
            )
          })()}

          {/* 2c. Storico rilavorazioni — confronto vecchio / rilavorato */}
          {versions.length > 0 && (() => {
            // Raggruppa per ciclo di rilavorazione, dal più recente
            const cycles = Array.from(new Set(versions.map(v => v.reworkCycle))).sort((a, b) => b - a)
            const fmtDate = (d: string) => new Date(d).toLocaleDateString('it-IT', { day: '2-digit', month: 'short' }) + ' ' + new Date(d).toLocaleTimeString('it-IT', { hour: '2-digit', minute: '2-digit' })
            return (
              <div className="mx-4 sm:mx-6 mt-4">
                <button
                  type="button"
                  onClick={() => setShowHistory(v => !v)}
                  className="w-full flex items-center justify-between gap-2 px-3 py-2 rounded-lg border border-slate-200 bg-slate-50 hover:bg-slate-100 transition-colors"
                >
                  <span className="flex items-center gap-2 text-xs font-semibold text-slate-600 uppercase tracking-wide">
                    <History className="w-3.5 h-3.5" />
                    Storico rilavorazioni ({cycles.length})
                  </span>
                  <ChevronDown className={cn('w-4 h-4 text-slate-400 transition-transform', showHistory && 'rotate-180')} />
                </button>

                {showHistory && (
                  <div className="mt-2 space-y-3">
                    {cycles.map(cycle => {
                      const before = versions.find(v => v.reworkCycle === cycle && v.stage === 'before')
                      const after = versions.find(v => v.reworkCycle === cycle && v.stage === 'after')
                      const Col = ({ v, label, tone }: { v: ContentVersion | undefined; label: string; tone: 'old' | 'new' }) => (
                        <div className={cn('flex-1 min-w-0 rounded-lg border p-2.5', tone === 'old' ? 'border-slate-200 bg-slate-50' : 'border-success-200 bg-success-50/50')}>
                          <p className={cn('text-[10px] font-semibold uppercase tracking-wide mb-1.5', tone === 'old' ? 'text-slate-400' : 'text-success-600')}>{label}</p>
                          {v ? (
                            <>
                              <p className="text-sm text-slate-700 whitespace-pre-wrap break-words">{v.caption?.trim() || v.body?.trim() || <span className="italic text-slate-400">nessuna copy</span>}</p>
                              {v.hashtags?.trim() && <p className="text-xs text-primary-600 mt-1.5 break-words">{hashtagsToArray(v.hashtags).map(t => `#${t}`).join(' ')}</p>}
                              {v.assets && v.assets.length > 0 && (
                                <div className="mt-2 space-y-1">
                                  {v.assets.map((a, i) => (
                                    <div key={i} className="flex items-center gap-1.5 text-[11px] text-slate-500">
                                      <AssetIcon mimeType={a.mimeType} className="w-3 h-3 flex-shrink-0" />
                                      <span className="truncate">{a.filename}</span>
                                    </div>
                                  ))}
                                </div>
                              )}
                              <p className="text-[10px] text-slate-400 mt-2 tabular-nums">
                                {v.createdBy ? `${v.createdBy.firstName} ${v.createdBy.lastName} — ` : ''}{fmtDate(v.createdAt)}
                              </p>
                            </>
                          ) : (
                            <p className="text-xs italic text-slate-400">in corso…</p>
                          )}
                        </div>
                      )
                      return (
                        <div key={cycle} className="rounded-lg border border-slate-200 overflow-hidden">
                          <div className="px-3 py-1.5 bg-slate-100 text-[11px] font-medium text-slate-500">Rilavorazione #{cycle}</div>
                          <div className="flex items-stretch gap-2 p-2">
                            <Col v={before} label="Vecchio" tone="old" />
                            <div className="flex items-center"><ArrowRight className="w-4 h-4 text-slate-300" /></div>
                            <Col v={after} label="Rilavorato" tone="new" />
                          </div>
                        </div>
                      )
                    })}
                  </div>
                )}
              </div>
            )
          })()}

          {/* 3. Dettagli contenuto */}
          <div className="px-4 sm:px-6 py-3 sm:py-4 space-y-4 sm:space-y-5">

            {/* Programmazione */}
            <div>
              <p className="text-xs font-semibold text-slate-500 uppercase tracking-wide mb-2 flex items-center gap-1.5">
                <Calendar className="w-3.5 h-3.5" />
                Programmazione
              </p>
              {editingField === 'schedule' ? (
                <div className="flex items-center gap-2">
                  <input
                    type="datetime-local"
                    value={editScheduledAt}
                    onChange={(e) => setEditScheduledAt(e.target.value)}
                    className="text-sm px-3 py-1.5 border border-slate-200 rounded-lg focus:outline-none focus:border-primary-400 focus:ring-1 focus:ring-primary-400/20 tabular-nums"
                  />
                  <button onClick={saveSchedule} className="p-1.5 bg-primary-600 text-white rounded-lg hover:bg-primary-700 transition-colors">
                    <CheckIcon className="w-3.5 h-3.5" />
                  </button>
                  <button onClick={() => { setEditScheduledAt(content.scheduledAt ? new Date(content.scheduledAt).toISOString().slice(0, 16) : ''); setEditingField(null) }} className="p-1.5 text-slate-400 hover:text-slate-600 transition-colors">
                    <X className="w-3.5 h-3.5" />
                  </button>
                </div>
              ) : (
                <div onClick={() => setEditingField('schedule')} className="cursor-pointer group" title="Clicca per modificare">
                  {scheduledDate || scheduledTime ? (
                    <div className="flex items-center gap-4 group-hover:text-primary-700 transition-colors">
                      {scheduledDate && (
                        <div className="flex items-center gap-2 text-sm text-slate-600 group-hover:text-primary-700">
                          <Calendar className="w-4 h-4 text-slate-400 flex-shrink-0 group-hover:text-primary-500" />
                          <span className="tabular-nums">{scheduledDate}</span>
                        </div>
                      )}
                      {scheduledTime && (
                        <div className="flex items-center gap-2 text-sm text-slate-600 group-hover:text-primary-700">
                          <Clock className="w-4 h-4 text-slate-400 flex-shrink-0 group-hover:text-primary-500" />
                          <span className="tabular-nums">{scheduledTime}</span>
                        </div>
                      )}
                      <Pencil className="w-3 h-3 text-slate-300 opacity-0 group-hover:opacity-100 transition-opacity" />
                    </div>
                  ) : (
                    <button className="text-xs text-slate-400 hover:text-primary-600 border border-dashed border-slate-300 hover:border-primary-400 rounded-lg px-3 py-2 transition-colors">
                      + Aggiungi data di pubblicazione
                    </button>
                  )}
                </div>
              )}
            </div>

            {/* Caption */}
            <div>
              <p className="text-xs font-semibold text-slate-500 uppercase tracking-wide mb-2 flex items-center gap-1.5">
                <MessageSquare className="w-3.5 h-3.5" />
                Caption
              </p>
              {editingField === 'caption' ? (
                <div>
                  <textarea
                    ref={captionRef}
                    value={editCaption}
                    onChange={(e) => setEditCaption(e.target.value)}
                    rows={5}
                    className="w-full text-sm text-slate-700 leading-relaxed bg-white rounded-lg p-3 border-2 border-primary-500 outline-none resize-y"
                    placeholder="Scrivi la caption..."
                  />
                  <div className="flex justify-end gap-2 mt-1.5">
                    <button onClick={() => { setEditCaption(content.caption ?? content.body ?? ''); setEditingField(null) }} className="px-3 py-1 text-xs text-slate-500 hover:text-slate-700 transition-colors">Annulla</button>
                    <button onClick={saveCaption} className="px-3 py-1 text-xs font-medium text-white bg-primary-600 rounded-lg hover:bg-primary-700 transition-colors">Salva</button>
                  </div>
                </div>
              ) : (
                <div onClick={() => { setEditCaption(content.caption ?? content.body ?? ''); setEditingField('caption') }} className="cursor-pointer group" title="Clicca per modificare">
                  {(content.caption ?? content.body) ? (
                    <div className="relative">
                      <p className="text-sm text-slate-700 leading-relaxed bg-slate-50 rounded-lg p-3 border border-slate-200 group-hover:border-primary-300 transition-colors whitespace-pre-wrap">
                        {content.caption ?? content.body}
                      </p>
                      <Pencil className="absolute top-2 right-2 w-3 h-3 text-slate-300 opacity-0 group-hover:opacity-100 transition-opacity" />
                    </div>
                  ) : (
                    <button className="w-full text-xs text-slate-400 hover:text-primary-600 border border-dashed border-slate-300 hover:border-primary-400 rounded-lg px-3 py-4 transition-colors text-center">
                      + Aggiungi caption
                    </button>
                  )}
                </div>
              )}
            </div>

            {/* Format */}
            {content.socialFormat && (
              <div ref={formatDetailRef}>
                <p className="text-xs font-semibold text-slate-500 uppercase tracking-wide mb-2 flex items-center gap-1.5">
                  <Layers className="w-3.5 h-3.5" />
                  Format
                </p>
                <button
                  onClick={() => setShowFormatDetail(!showFormatDetail)}
                  className="inline-flex items-center gap-2 px-3 py-2 text-sm font-medium text-primary-700 bg-primary-50 border border-primary-200 rounded-lg hover:bg-primary-100 transition-colors cursor-pointer"
                >
                  {content.socialFormat.name}
                  <ChevronDown className={cn('w-3.5 h-3.5 transition-transform', showFormatDetail && 'rotate-180')} />
                </button>
                {showFormatDetail && (
                  <div className="mt-2 bg-white border border-slate-200 rounded-2xl shadow-card p-4 space-y-3">
                    {content.socialFormat.description && (
                      <p className="text-sm text-slate-700 leading-relaxed">{content.socialFormat.description}</p>
                    )}
                    {content.socialFormat.specs && (
                      <div>
                        <p className="text-[10px] font-semibold text-slate-400 uppercase tracking-wider mb-1.5">Specifiche</p>
                        <div className="grid grid-cols-2 gap-2">
                          {content.socialFormat.specs.width && content.socialFormat.specs.height && (
                            <div className="bg-slate-50 rounded-lg px-3 py-1.5">
                              <span className="text-[10px] text-slate-400">Dimensioni</span>
                              <p className="text-xs font-semibold text-slate-700 tabular-nums">{content.socialFormat.specs.width}x{content.socialFormat.specs.height} px</p>
                            </div>
                          )}
                          {content.socialFormat.specs.aspectRatio && (
                            <div className="bg-slate-50 rounded-lg px-3 py-1.5">
                              <span className="text-[10px] text-slate-400">Aspect Ratio</span>
                              <p className="text-xs font-semibold text-slate-700 tabular-nums">{content.socialFormat.specs.aspectRatio}</p>
                            </div>
                          )}
                          {content.socialFormat.specs.duration && (
                            <div className="bg-slate-50 rounded-lg px-3 py-1.5">
                              <span className="text-[10px] text-slate-400">Durata</span>
                              <p className="text-xs font-semibold text-slate-700 tabular-nums">{content.socialFormat.specs.duration}</p>
                            </div>
                          )}
                          {content.socialFormat.specs.maxChars && (
                            <div className="bg-slate-50 rounded-lg px-3 py-1.5">
                              <span className="text-[10px] text-slate-400">Max caratteri</span>
                              <p className="text-xs font-semibold text-slate-700 tabular-nums">{content.socialFormat.specs.maxChars}</p>
                            </div>
                          )}
                          {content.socialFormat.specs.slides && (
                            <div className="bg-slate-50 rounded-lg px-3 py-1.5">
                              <span className="text-[10px] text-slate-400">Slide</span>
                              <p className="text-xs font-semibold text-slate-700 tabular-nums">{content.socialFormat.specs.slides}</p>
                            </div>
                          )}
                        </div>
                      </div>
                    )}
                    {content.socialFormat.guidelines && (
                      <div>
                        <p className="text-[10px] font-semibold text-slate-400 uppercase tracking-wider mb-1">Linee guida</p>
                        <p className="text-sm text-slate-600 leading-relaxed whitespace-pre-wrap">{content.socialFormat.guidelines}</p>
                      </div>
                    )}
                  </div>
                )}
              </div>
            )}

            {/* Hashtags */}
            <div>
              <p className="text-xs font-semibold text-slate-500 uppercase tracking-wide mb-2 flex items-center gap-1.5">
                <Hash className="w-3.5 h-3.5" />
                Hashtag
              </p>
              {editingField === 'hashtags' ? (
                <div>
                  <input
                    type="text"
                    value={editHashtags}
                    onChange={(e) => setEditHashtags(e.target.value)}
                    onKeyDown={(e) => { if (e.key === 'Enter') saveHashtags(); if (e.key === 'Escape') { setEditHashtags(hashtagsToString(content.hashtags)); setEditingField(null) } }}
                    placeholder="tag1, tag2, tag3..."
                    className="w-full text-sm px-3 py-2 border-2 border-primary-500 rounded-lg outline-none"
                  />
                  <p className="text-[10px] text-slate-400 mt-1">Separati da virgola o spazio. Premi Enter per salvare.</p>
                  <div className="flex justify-end gap-2 mt-1.5">
                    <button onClick={() => { setEditHashtags(hashtagsToString(content.hashtags)); setEditingField(null) }} className="px-3 py-1 text-xs text-slate-500 hover:text-slate-700 transition-colors">Annulla</button>
                    <button onClick={saveHashtags} className="px-3 py-1 text-xs font-medium text-white bg-primary-600 rounded-lg hover:bg-primary-700 transition-colors">Salva</button>
                  </div>
                </div>
              ) : (
                <div onClick={() => { setEditHashtags(hashtagsToString(content.hashtags)); setEditingField('hashtags') }} className="cursor-pointer group" title="Clicca per modificare">
                  {hashtagsToArray(content.hashtags).length > 0 ? (
                    <div className="flex flex-wrap gap-1.5 relative">
                      {hashtagsToArray(content.hashtags).map((tag) => (
                        <span key={tag} className="text-xs text-primary-700 bg-primary-50 px-2 py-0.5 rounded-full border border-primary-200 group-hover:border-primary-400 transition-colors">
                          #{tag}
                        </span>
                      ))}
                      <Pencil className="w-3 h-3 text-slate-300 opacity-0 group-hover:opacity-100 transition-opacity self-center" />
                    </div>
                  ) : (
                    <button className="text-xs text-slate-400 hover:text-primary-600 border border-dashed border-slate-300 hover:border-primary-400 rounded-lg px-3 py-2 transition-colors">
                      + Aggiungi hashtag
                    </button>
                  )}
                </div>
              )}
            </div>

            {/* Allegati */}
            <ContentAssetsSection contentId={content.id} />

            {/* Autore */}
            {content.creator && (
              <div>
                <p className="text-xs font-semibold text-slate-500 uppercase tracking-wide mb-2 flex items-center gap-1.5">
                  <User className="w-3.5 h-3.5" />
                  Autore
                </p>
                <div className="flex items-center gap-2">
                  <div className="w-7 h-7 rounded-full bg-primary-600 flex items-center justify-center flex-shrink-0">
                    <span className="text-[10px] font-semibold text-white">
                      {content.creator.firstName.charAt(0)}{content.creator.lastName.charAt(0)}
                    </span>
                  </div>
                  <span className="text-sm text-slate-700 font-medium">
                    {content.creator.firstName} {content.creator.lastName}
                  </span>
                  <Badge variant="neutral" size="sm" className="capitalize">
                    {content.creator.role?.name?.toLowerCase() ?? ''}
                  </Badge>
                </div>
              </div>
            )}

            {/* Reparto */}
            {(content as any).department && (
              <div>
                <p className="text-xs font-semibold text-slate-500 uppercase tracking-wide mb-2 flex items-center gap-1.5">
                  <Building2 className="w-3.5 h-3.5" />
                  Reparto
                </p>
                <div className="flex items-center gap-2">
                  <div className="w-7 h-7 rounded-lg bg-primary-100 flex items-center justify-center flex-shrink-0">
                    <Building2 className="w-3.5 h-3.5 text-primary-600" />
                  </div>
                  <span className="text-sm text-slate-700 font-medium">
                    {(content as any).department.name}
                  </span>
                </div>
              </div>
            )}

            {/* Assegnato a (producer/videomaker) */}
            <div>
              <p className="text-xs font-semibold text-slate-500 uppercase tracking-wide mb-2 flex items-center gap-1.5">
                <UserPlus className="w-3.5 h-3.5" />
                Assegnato a
              </p>
              {content.assignedTo ? (
                <div className="flex items-center gap-2">
                  <div className="w-7 h-7 rounded-full bg-primary-600 flex items-center justify-center flex-shrink-0">
                    <span className="text-[10px] font-semibold text-white">
                      {content.assignedTo.firstName.charAt(0)}{content.assignedTo.lastName.charAt(0)}
                    </span>
                  </div>
                  <span className="text-sm text-slate-700 font-medium">
                    {content.assignedTo.firstName} {content.assignedTo.lastName}
                  </span>
                  {content.assignedTo.role?.name && (
                    <Badge variant="neutral" size="sm" className="capitalize">
                      {content.assignedTo.role.name.toLowerCase()}
                    </Badge>
                  )}
                  <button
                    onClick={() => setShowAssignedPicker(!showAssignedPicker)}
                    className="ml-auto text-[10px] text-primary-600 hover:text-primary-700 font-medium"
                  >
                    Cambia
                  </button>
                  <button
                    onClick={() => onUpdate?.(content.id, { assignedToId: null })}
                    className="text-[10px] text-slate-400 hover:text-danger-600 font-medium"
                  >
                    Rimuovi
                  </button>
                </div>
              ) : (
                <button
                  onClick={() => setShowAssignedPicker(!showAssignedPicker)}
                  className="text-xs text-slate-400 hover:text-primary-600 border border-dashed border-slate-300 hover:border-primary-400 rounded-lg px-3 py-2 transition-colors"
                >
                  + Assegna persona
                </button>
              )}
              {showAssignedPicker && (
                <div className="mt-2 bg-white border border-slate-200 rounded-lg shadow-dropdown p-1 max-h-48 overflow-y-auto">
                  {allUsers.map(u => (
                    <button
                      key={u.id}
                      onClick={() => {
                        onUpdate?.(content.id, { assignedToId: u.id })
                        setShowAssignedPicker(false)
                      }}
                      className={cn(
                        'w-full flex items-center gap-2 px-3 py-2 text-sm rounded hover:bg-slate-50 transition-colors text-left',
                        u.id === content.assignedToId && 'bg-primary-50 text-primary-700'
                      )}
                    >
                      <div className="w-6 h-6 rounded-full bg-primary-600 flex items-center justify-center flex-shrink-0">
                        <span className="text-[9px] font-semibold text-white">
                          {u.firstName.charAt(0)}{u.lastName.charAt(0)}
                        </span>
                      </div>
                      <span className="text-slate-700">{u.firstName} {u.lastName}</span>
                      {u.role?.name && (
                        <span className="text-[10px] text-slate-400 ml-auto capitalize">{u.role.name.toLowerCase()}</span>
                      )}
                    </button>
                  ))}
                </div>
              )}
            </div>

            {/* Pubblicato da (social manager) */}
            <div>
              <p className="text-xs font-semibold text-slate-500 uppercase tracking-wide mb-2 flex items-center gap-1.5">
                <Send className="w-3.5 h-3.5" />
                Pubblicato da
              </p>
              {content.publishedBy ? (
                <div className="flex items-center gap-2">
                  <div className="w-7 h-7 rounded-full bg-success-600 flex items-center justify-center flex-shrink-0">
                    <span className="text-[10px] font-semibold text-white">
                      {content.publishedBy.firstName.charAt(0)}{content.publishedBy.lastName.charAt(0)}
                    </span>
                  </div>
                  <span className="text-sm text-slate-700 font-medium">
                    {content.publishedBy.firstName} {content.publishedBy.lastName}
                  </span>
                  {content.publishedBy.role?.name && (
                    <Badge variant="neutral" size="sm" className="capitalize">
                      {content.publishedBy.role.name.toLowerCase()}
                    </Badge>
                  )}
                  <button
                    onClick={() => setShowPublisherPicker(!showPublisherPicker)}
                    className="ml-auto text-[10px] text-primary-600 hover:text-primary-700 font-medium"
                  >
                    Cambia
                  </button>
                  <button
                    onClick={() => onUpdate?.(content.id, { publishedById: null })}
                    className="text-[10px] text-slate-400 hover:text-danger-600 font-medium"
                  >
                    Rimuovi
                  </button>
                </div>
              ) : (
                <button
                  onClick={() => setShowPublisherPicker(!showPublisherPicker)}
                  className="text-xs text-slate-400 hover:text-primary-600 border border-dashed border-slate-300 hover:border-primary-400 rounded-lg px-3 py-2 transition-colors"
                >
                  + Assegna publisher
                </button>
              )}
              {showPublisherPicker && (
                <div className="mt-2 bg-white border border-slate-200 rounded-lg shadow-dropdown p-1 max-h-48 overflow-y-auto">
                  {allUsers.map(u => (
                    <button
                      key={u.id}
                      onClick={() => {
                        onUpdate?.(content.id, { publishedById: u.id })
                        setShowPublisherPicker(false)
                      }}
                      className={cn(
                        'w-full flex items-center gap-2 px-3 py-2 text-sm rounded hover:bg-slate-50 transition-colors text-left',
                        u.id === content.publishedById && 'bg-primary-50 text-primary-700'
                      )}
                    >
                      <div className="w-6 h-6 rounded-full bg-success-600 flex items-center justify-center flex-shrink-0">
                        <span className="text-[9px] font-semibold text-white">
                          {u.firstName.charAt(0)}{u.lastName.charAt(0)}
                        </span>
                      </div>
                      <span className="text-slate-700">{u.firstName} {u.lastName}</span>
                      {u.role?.name && (
                        <span className="text-[10px] text-slate-400 ml-auto capitalize">{u.role.name.toLowerCase()}</span>
                      )}
                    </button>
                  ))}
                </div>
              )}
            </div>

            {/* ── Commenti e Feedback ── */}
            <div>
              <p className="text-xs font-semibold text-slate-500 uppercase tracking-wide mb-2 flex items-center gap-1.5">
                <MessageSquare className="w-3.5 h-3.5" />
                Commenti e Feedback
                {comments.length > 0 && (
                  <span className="text-[10px] text-slate-400 font-normal ml-1">({comments.length})</span>
                )}
              </p>

              {/* Comments list */}
              <div className="space-y-2.5 max-h-60 overflow-y-auto">
                {comments.length === 0 && (
                  <div className="bg-slate-50 rounded-lg border border-dashed border-slate-300 p-4 text-center">
                    <p className="text-xs text-slate-400">Nessun commento ancora.</p>
                  </div>
                )}
                {comments.map((c) => {
                  const typeStyle = COMMENT_TYPE_STYLE[c.type] ?? COMMENT_TYPE_STYLE.comment!
                  const isSystem = c.type !== 'comment'
                  return (
                    <div
                      key={c.id}
                      className={cn(
                        'rounded-lg p-3',
                        isSystem
                          ? `border ${typeStyle.color}`
                          : 'bg-slate-50 border border-slate-200'
                      )}
                    >
                      <div className="flex items-center gap-2 mb-1">
                        <div className="w-5 h-5 rounded-full bg-primary-600 flex items-center justify-center flex-shrink-0">
                          <span className="text-[8px] font-semibold text-white">
                            {c.user.firstName.charAt(0)}{c.user.lastName.charAt(0)}
                          </span>
                        </div>
                        <span className="text-xs font-medium text-slate-700">
                          {c.user.firstName} {c.user.lastName}
                        </span>
                        {isSystem && typeStyle.label && (
                          <span className={cn('text-[10px] font-medium px-1.5 py-0.5 rounded-full', typeStyle.color)}>
                            {typeStyle.label}
                          </span>
                        )}
                        <span className="text-[10px] text-slate-400 ml-auto tabular-nums">
                          {new Date(c.createdAt).toLocaleDateString('it-IT', { day: '2-digit', month: 'short' })}
                          {' '}
                          {new Date(c.createdAt).toLocaleTimeString('it-IT', { hour: '2-digit', minute: '2-digit' })}
                        </span>
                      </div>
                      <p className="text-sm text-slate-700 leading-relaxed">{c.text}</p>
                    </div>
                  )
                })}
                <div ref={commentsEndRef} />
              </div>

              {/* Change request banner */}
              {changeRequestMode && (
                <div className="flex items-center gap-2 mt-3 px-3 py-2 bg-warning-50 border border-warning-200 rounded-lg">
                  <AlertTriangle className="w-4 h-4 text-warning-600 flex-shrink-0" />
                  <p className="text-xs text-warning-700 font-medium">
                    Scrivi il motivo della richiesta di modifiche, poi premi Invio
                  </p>
                  <button
                    onClick={() => { setChangeRequestMode(false); setCommentText('') }}
                    className="ml-auto text-[10px] text-warning-600 hover:text-warning-800 font-medium"
                  >
                    Annulla
                  </button>
                </div>
              )}

              {approveCommentMode && (
                <div className="flex items-center gap-2 mt-3 px-3 py-2 bg-success-50 border border-success-200 rounded-lg">
                  <CheckIcon className="w-4 h-4 text-success-600 flex-shrink-0" />
                  <p className="text-xs text-success-700 font-medium">
                    Scrivi la nota di approvazione, poi premi Invio per approvare
                  </p>
                  <button
                    onClick={() => { setApproveCommentMode(false); setCommentText('') }}
                    className="ml-auto text-[10px] text-success-600 hover:text-success-800 font-medium"
                  >
                    Annulla
                  </button>
                </div>
              )}

              {/* Comment input */}
              <div className="flex gap-2 mt-2">
                <input
                  ref={commentInputRef}
                  type="text"
                  value={commentText}
                  onChange={(e) => setCommentText(e.target.value)}
                  onKeyDown={(e) => { if (e.key === 'Enter' && commentText.trim()) handleSubmitComment() }}
                  placeholder={approveCommentMode ? 'Nota di approvazione...' : changeRequestMode ? 'Descrivi le modifiche richieste...' : 'Aggiungi un commento...'}
                  className={cn(
                    'flex-1 text-sm px-3 py-2 border rounded-lg focus:outline-none placeholder:text-slate-400 transition-colors',
                    changeRequestMode
                      ? 'border-warning-400 focus:border-warning-500 focus:ring-1 focus:ring-warning-500/20 bg-warning-50/50'
                      : approveCommentMode
                        ? 'border-success-400 focus:border-success-500 focus:ring-1 focus:ring-success-500/20 bg-success-50/50'
                        : 'border-slate-200 focus:border-primary-400 focus:ring-1 focus:ring-primary-400/20'
                  )}
                />
                <button
                  onClick={handleSubmitComment}
                  disabled={!commentText.trim() || addCommentMutation.isPending}
                  className={cn(
                    'p-2 text-white rounded-lg transition-colors disabled:opacity-40',
                    changeRequestMode
                      ? 'bg-warning-600 hover:bg-warning-700'
                      : approveCommentMode
                        ? 'bg-success-600 hover:bg-success-700'
                        : 'bg-primary-600 hover:bg-primary-700'
                  )}
                >
                  <Send className="w-4 h-4" />
                </button>
              </div>
            </div>
          </div>
        </div>

        {/* ── Footer actions — hidden for L4 Operative ── */}
        {content.status !== 'PUBLISHED' && userLevel <= 3 && (
          <div className="border-t border-slate-200 px-4 sm:px-6 py-3 sm:py-4 bg-white flex items-center gap-2 sm:gap-3">
            {content.status === 'IN_REVIEW' && canApproveOrReject ? (
              <>
                <button
                  onClick={handleApprove}
                  className="flex-1 px-3 sm:px-4 py-2 text-xs sm:text-sm font-medium text-white bg-success-600 rounded-lg hover:bg-success-700 transition-colors"
                >
                  Approva
                </button>
                <button
                  onClick={() => { setChangeRequestMode(false); setApproveCommentMode(true) }}
                  className="flex-1 px-3 sm:px-4 py-2 text-xs sm:text-sm font-medium text-success-700 bg-success-50 border border-success-200 rounded-lg hover:bg-success-100 transition-colors"
                >
                  <span className="hidden sm:inline">Approva con commento</span>
                  <span className="sm:hidden">+ Nota</span>
                </button>
                <button
                  onClick={() => { setApproveCommentMode(false); handleRequestChanges() }}
                  className="flex-1 px-3 sm:px-4 py-2 text-xs sm:text-sm font-medium text-warning-700 bg-white border border-warning-300 rounded-lg hover:bg-warning-50 transition-colors"
                >
                  Revisione
                </button>
              </>
            ) : (
              <>
                {canRequestChanges && (
                  <button
                    onClick={handleRequestChanges}
                    className="flex-1 px-3 sm:px-4 py-2 text-xs sm:text-sm font-medium text-slate-700 bg-white border border-slate-200 rounded-lg hover:bg-slate-50 transition-colors"
                  >
                    <span className="hidden sm:inline">Richiedi Modifiche</span>
                    <span className="sm:hidden">Modifiche</span>
                  </button>
                )}
                {effectiveAdvanceLabel && !(!canApproveOrReject && (content.status === 'IN_REVIEW' || content.status === 'APPROVED')) && (
                  <button
                    onClick={handleAdvance}
                    className="flex-1 px-3 sm:px-4 py-2 text-xs sm:text-sm font-medium text-white bg-primary-600 rounded-lg hover:bg-primary-700 transition-colors"
                  >
                    {effectiveAdvanceLabel}
                  </button>
                )}
              </>
            )}
          </div>
        )}
      </div>
    </>
  )
}
